import React, { useState, forwardRef } from 'react';
import Input from './Input';
import { validateEmail } from '../../utils/validation';
import { suggestEmailDomain } from '../../utils/email-domain-suggestions';

type EmailInputProps = {
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSuggestionAccept?: (email: string) => void;
  externalSuggestion?: string | null;
  error?: string | null;
  label?: string;
  optional?: boolean;
};

const EmailInput = forwardRef<HTMLInputElement, EmailInputProps>(({
  value,
  onChange,
  onSuggestionAccept,
  externalSuggestion,
  error: externalError,
  label = 'Email Address',
  optional = false,
}, ref) => {
  const [internalError, setInternalError] = useState<string | null>(null);
  const [internalSuggestion, setInternalSuggestion] = useState<string | null>(null);

  const error = externalError || internalError;
  const suggestion = externalSuggestion ?? internalSuggestion;

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const emailValue = e.target.value;
    if (!emailValue) return;
    setInternalError(validateEmail(emailValue));
    setInternalSuggestion(suggestEmailDomain(emailValue));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInternalSuggestion(null);
    onChange(e);
  };

  const handleSuggestionClick = () => {
    if (!suggestion) return;
    const accepted = suggestion;
    setInternalSuggestion(null);
    setInternalError(null);
    onSuggestionAccept?.(accepted);
  };

  return (
    <div>
      <Input
        ref={ref}
        type="email"
        name="email"
        id="email"
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        error={error}
        label={label}
        optional={optional}
        placeholder="your@email.com"
        required={!optional}
      />
      {suggestion && !error && (
        <p className="mt-1 text-sm text-gray-500" role="status" aria-live="polite">
          Did you mean{' '}
          <button
            type="button"
            onClick={handleSuggestionClick}
            className="text-blue-600 font-medium underline underline-offset-2 hover:text-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
          >
            {suggestion}
          </button>
          ? If the email entered is correct, click continue.
        </p>
      )}
    </div>
  );
});

EmailInput.displayName = 'EmailInput';

export default EmailInput;
